import { Alert, AlertDescription, AlertIcon } from '@chakra-ui/alert';
import {
  Box,
  HStack,
  IconButton,
  Skeleton,
  Text,
  VStack
} from '@chakra-ui/react';
import React, { FC, useCallback, useMemo, useState } from 'react';

import { CopyButton } from '@app/components/CopyButton';
import { DebouncedInput } from '@app/components/DebouncedInput';
import { QuickAmountButtons } from '@app/components/Swap/components/QuickAmountButtons';
import {
  formatWalletAddress,
  kFormatter,
  toOptionalFixed
} from '@app/helpers/format';
import { useWithdrawLiquidity } from '@app/hooks/liquidity/useWithdrawLiquidity';
import { useWithdrawLiquidityOutput } from '@app/hooks/liquidity/useWithdrawLiquidityOutput';
import { WithdrawLiquiditySubmitButton } from '@app/screens/pair-details/WithdrawLiquiditySubmitButton';
import { Pair } from '@app/types/pool';
import s from '@app/components/Swap/Swap.module.scss';
import { APP_ROUTES } from '@app/constants/routes';
import { Icon } from '@app/components/Icon';
import { HeaderControls } from '@app/components/Swap/components/HeaderControls';
import { useRouter } from 'next/router';
import { TokenIcon } from '@app/components/TokenIcon';
import { useTokenBalance } from '@app/hooks/token/useTokenBalance';
import { useAddress } from '@thirdweb-dev/react';
import { useTokenPriceV3 } from '@app/hooks/token/useTokenPriceV3';
import { EMPTY_ADDRESS } from '@app/constants/common';

interface Props {
  pair: Pair;
  balance: number;
  getWithdrawBalance: () => Promise<number | void>;
  onClose?: () => void;
}

export const WithdrawLiquidity: FC<Props> = ({
  pair,
  balance,
  getWithdrawBalance,
  onClose
}) => {
  const router = useRouter();
  const address = useAddress();
  const [amount, setAmountValue] = useState('');

  const setAmount = useCallback((newValue: string) => {
    setAmountValue((+newValue).toFixed(18));
  }, []);

  const {
    token0,
    token1,
    totalSupply,
    reserveETH,
    reserve0,
    reserve1,
    id,
    token1Price,
    token0Price,
    liquidityPositions
  } = pair;

  const {
    withdrawLiquidity,
    withdrawingLiquidity,
    allowance,
    approveWithdraw,
    approvingWithdraw
  } = useWithdrawLiquidity(pair, amount);

  const { output, loading } = useWithdrawLiquidityOutput(pair.id, amount, pair);

  const { balance: balance0 } = useTokenBalance(token0.id, id);
  const { balance: balance1 } = useTokenBalance(token1.id, id);

  const liquidityTokenBalance = liquidityPositions.find(
    item => item.user.id.toLowerCase() === address?.toLowerCase()
  )?.liquidityTokenBalance;

  const composition = useMemo(() => {
    if (
      balance0 === undefined ||
      balance1 === undefined ||
      !liquidityTokenBalance
    ) {
      return;
    }

    const amount0 = (+liquidityTokenBalance * balance0) / +totalSupply;
    const amount1 = (+liquidityTokenBalance * balance1) / +totalSupply;

    return {
      amount0,
      amount1
    };
  }, [liquidityTokenBalance, balance0, balance1, totalSupply]);

  const { data: price, isLoading: loadingPrice } = useTokenPriceV3({
    id: 'weth'
  });

  const poolShare = useMemo(() => {
    if (!totalSupply || !liquidityTokenBalance) {
      return 0;
    }

    return (+liquidityTokenBalance * 100) / +totalSupply;
  }, [totalSupply, liquidityTokenBalance]);

  const amountValueUSD = useMemo(() => {
    if (!price || !liquidityTokenBalance) {
      return null;
    }

    const liqBalancePercent = (+liquidityTokenBalance * 100) / +totalSupply;
    const liqBalanceEth = (+reserveETH * liqBalancePercent) / 100;

    return kFormatter(liqBalanceEth * +price, 2);
  }, [liquidityTokenBalance, price, totalSupply, reserveETH]);

  function renderInfoRow(
    label: string,
    value: string | number,
    loading?: boolean,
    copyValue?: boolean,
    rawValue?: string
  ) {
    function renderContent() {
      if (loading) {
        return (
          <Skeleton
            height="16px"
            width="64px"
            startColor="neutral.700"
            endColor="neutral.800"
            mb="2px"
          />
        );
      }

      return copyValue && rawValue ? (
        <HStack>
          <Text>{value}</Text>
          <CopyButton text={rawValue} />
        </HStack>
      ) : (
        <Text>{value}</Text>
      );
    }

    return (
      <HStack justifyContent="space-between" mb={2} color="whiteOpacity.70">
        <Text fontWeight={400} fontSize="14px">
          {label}
        </Text>
        {renderContent()}
      </HStack>
    );
  }

  return (
    <Box
      bg="bg.1"
      borderRadius={8}
      mt="64px"
      maxWidth="424px"
      mx="auto"
      w="100%"
      sx={{
        boxShadow: '0 8px 140px 0 rgba(126, 229, 117, 0.70)'
      }}
    >
      <HStack justifyContent="space-between" width="100%" className={s.header}>
        <IconButton
          aria-label="go back"
          onClick={() => router.push(APP_ROUTES.positions)}
        >
          <Icon name="goBack" />
        </IconButton>
        <Text className={s.title}>Remove liquidity</Text>
        <HeaderControls />
      </HStack>

      <VStack w="100%" gap={0}>
        <div className={s.divider} />
        <div className={s.content}>
          <div className={s.caption}>My Position</div>
          <HStack w="100%" justifyContent="space-between" mb="14px">
            <div className={s.label}>My Share</div>
            <div className={s.value}>{toOptionalFixed(poolShare, 5)}%</div>
          </HStack>
          <HStack w="100%" justifyContent="space-between" mb="14px">
            <div className={s.label}>Pooled {token0.symbol}</div>
            <div className={s.value}>
              {composition ? (
                toOptionalFixed(composition.amount0, 5)
              ) : (
                <Skeleton
                  height="21px"
                  width="48px"
                  startColor="gray.1"
                  endColor="gray.3"
                />
              )}
              <TokenIcon symbol={token0.symbol} />
            </div>
          </HStack>
          <HStack w="100%" justifyContent="space-between">
            <div className={s.label}>Pooled {token1.symbol}</div>
            <div className={s.value}>
              {composition ? (
                toOptionalFixed(composition.amount1, 5)
              ) : (
                <Skeleton
                  height="21px"
                  width="48px"
                  startColor="gray.1"
                  endColor="gray.3"
                />
              )}
              <TokenIcon symbol={token1.symbol} />
            </div>
          </HStack>
        </div>
        <div className={s.divider} />
        <div className={s.content}>
          <div className={s.caption}>Remove Amount</div>
          <div style={{ display: 'none' }}>
            <DebouncedInput
              textAlign="left"
              amount={amount}
              onChange={val => {
                setAmount(val);
              }}
            />
            <HStack w="100%" justify="space-between">
              <HStack justify="flex-start">
                <Text
                  color="neutral.500"
                  fontSize="12px"
                  fontWeight="400"
                  lineHeight="18px"
                  letterSpacing="-0.18px"
                >
                  Balance:
                </Text>
                <Text>{balance}</Text>
              </HStack>
            </HStack>
          </div>

          <QuickAmountButtons
            amount={amount}
            balance={balance}
            onUpdate={val => setAmount(val)}
          />

          {output && (
            <>
              <div className={s.pointer}>
                <Icon name="arrowDownCircle" />
              </div>

              <div className={s.outputWrapper}>
                <div className={s.row}>
                  <TokenIcon symbol={token0.symbol} />
                  <span>
                    {output?.amount0 !== undefined
                      ? toOptionalFixed(Number(output?.amount0), 5)
                      : 'n/a'}
                  </span>
                  <span>{token0.symbol}</span>
                </div>
                <div className={s.row}>
                  <TokenIcon symbol={token1.symbol} />
                  <span>
                    {output?.amount1 !== undefined
                      ? toOptionalFixed(Number(output?.amount1), 5)
                      : 'n/a'}
                  </span>
                  <span>{token1.symbol}</span>
                </div>
              </div>
            </>
          )}
        </div>
      </VStack>
      <VStack className={s.footer}>
        <WithdrawLiquiditySubmitButton
          allowance={allowance}
          amount={amount}
          balance={balance}
          isWithdrawInProgress={withdrawingLiquidity}
          isApproveInProgress={approvingWithdraw}
          withdrawLiquidity={withdrawLiquidity}
          approveWithdraw={approveWithdraw}
          onClose={() => {
            onClose?.();
          }}
          getWithdrawBalance={getWithdrawBalance}
        />
      </VStack>
    </Box>
  );
};
